import { AxiosError } from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AUTH } from '@/api/constants/queryKeys';
import { AuthApi } from '@/api/auth/auth.api';
import { errorAndSuccessMethods, getResponseDetail, handleCatchBasedOnStatus } from '@/api/helpers';
import {
  IConfirmResetPasswordRequest,
  IConfirmResetPasswordResponse,
  ILogInRequest,
  ILogInResponse,
  IResetPasswordResponse,
  ISetOnboardingPasswordRequest,
  ISetOnboardingPasswordResponse,
  TUseConfirmResetPasswordOptions,
  TUseLogInUserOptions,
  TUseRefreshTokenOptions,
  TUseResetPasswordOptions,
  TUseSetOnboardingPasswordOptions,
} from '@/api/auth/auth.types';

const UNEXPECTED_LOG_IN_ERROR =
  'There was an unexpected issue. Our engineering team is already notified but you can also contact us at contact@agioratings.io.';
// the API's refusal per address (20 attempts in 5 minutes) comes without a detail
const TOO_MANY_LOG_IN_ATTEMPTS = 'Too many sign-in attempts. Try again in a few minutes.';

/**
 * The message the login form shows for a failed login. The API limits the attempts per address and the wrong
 * passwords per account (429, "Too many failed attempts. Try again in 15 minutes.") - a 429 says so, it is not an
 * unexpected issue.
 */
export const toLogInError = (error: AxiosError) => {
  if (error.response?.status === 429) {
    throw new Error(getResponseDetail(error.response.data) ?? TOO_MANY_LOG_IN_ATTEMPTS);
  }
  return handleCatchBasedOnStatus(error, 401, UNEXPECTED_LOG_IN_ERROR);
};

export const useLogInUser = (authApi: AuthApi, options?: TUseLogInUserOptions) => {
  const errorAndSuccess = !!options ? errorAndSuccessMethods(options) : {};
  const {
    error,
    isPending,
    mutate: logInUser,
  } = useMutation<ILogInResponse, AxiosError, ILogInRequest>({
    mutationKey: [AUTH],
    mutationFn: (logInCredentials: ILogInRequest) => {
      return authApi.logInUser(logInCredentials).catch(toLogInError);
    },
    ...errorAndSuccess,
  });

  return {
    error,
    isPending,
    logInUser,
  };
};

export const useRefreshToken = (authApi: AuthApi, options?: TUseRefreshTokenOptions) => {
  const errorAndSuccess = !!options ? errorAndSuccessMethods(options) : {};

  const { error, mutate: refreshToken } = useMutation<ILogInResponse, AxiosError>({
    mutationKey: [AUTH, 'refresh'],
    mutationFn: () => {
      return authApi.refreshToken().catch(error => {
        return handleCatchBasedOnStatus(
          error,
          401,
          'There was an unexpected issue. Our engineering team is already notified but you can also contact us at contact@agioratings.io.',
        );
      });
    },
    ...errorAndSuccess,
  });

  return {
    error,
    refreshToken,
  };
};

export const useResetPassword = (authApi: AuthApi, options?: TUseResetPasswordOptions) => {
  const errorAndSuccess = !!options ? errorAndSuccessMethods(options) : {};
  const {
    error,
    mutate: resetPassword,
    isPending,
  } = useMutation<IResetPasswordResponse, AxiosError, string>({
    mutationKey: [AUTH, 'reset-password'],
    mutationFn: (email: string) => {
      return authApi.resetPassword(email).catch(error => {
        return handleCatchBasedOnStatus(
          error,
          400,
          'There was an unexpected issue. Our engineering team is already notified but you can also contact us at contact@agioratings.io.',
        );
      });
    },
    ...errorAndSuccess,
  });

  return {
    error,
    resetPassword,
    isLoading: isPending,
  };
};

/**
 * Checks the reset link when the reset page opens, so a used or expired link says so before the user types a new
 * password. Only the API's 400 means the link is invalid - any other failure leaves the form usable, the
 * confirmation then answers.
 */
export const useCheckResetPassword = (authApi: AuthApi, token: string | null) => {
  const { isLoading, error } = useQuery<IConfirmResetPasswordResponse, AxiosError>({
    queryKey: [AUTH, 'check-reset-password', token],
    queryFn: () => authApi.checkResetPassword(token as string),
    enabled: !!token,
    retry: false,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

  return {
    isCheckingLink: isLoading,
    isLinkInvalid: error?.response?.status === 400,
  };
};

export const useConfirmResetPassword = (
  authApi: AuthApi,
  options?: TUseConfirmResetPasswordOptions,
) => {
  const errorAndSuccess = !!options ? errorAndSuccessMethods(options) : {};
  const {
    error,
    mutate: confirmResetPassword,
    isPending,
  } = useMutation<IConfirmResetPasswordResponse, AxiosError, IConfirmResetPasswordRequest>({
    mutationKey: [AUTH, 'confirm-reset-password'],
    mutationFn: (payload: IConfirmResetPasswordRequest) => {
      return authApi.confirmResetPassword(payload).catch(error => {
        return handleCatchBasedOnStatus(
          error,
          400,
          'There was an unexpected issue. Our engineering team is already notified but you can also contact us at contact@agioratings.io.',
        );
      });
    },
    ...errorAndSuccess,
  });

  return {
    error,
    confirmResetPassword,
    isLoading: isPending,
  };
};

export const useSetOnboardingPassword = (
  authApi: AuthApi,
  options?: TUseSetOnboardingPasswordOptions,
) => {
  const errorAndSuccess = !!options ? errorAndSuccessMethods(options) : {};
  const {
    error,
    mutate: setOnboardingPassword,
    isPending,
  } = useMutation<ISetOnboardingPasswordResponse, AxiosError, ISetOnboardingPasswordRequest>({
    mutationKey: [AUTH, 'onboarding-set-password'],
    mutationFn: (payload: ISetOnboardingPasswordRequest) => {
      return authApi.setOnboardingPassword(payload).catch(error => {
        return handleCatchBasedOnStatus(
          error,
          400,
          'There was an unexpected issue. Our engineering team is already notified but you can also contact us at contact@agioratings.io.',
        );
      });
    },
    ...errorAndSuccess,
  });

  return {
    error,
    setOnboardingPassword,
    isLoading: isPending,
  };
};
